import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Avatar,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import { CardGiftcard } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type Boleto = {
    id?: number;
    bono?: {
        id: number;
        name: string;
        price: number;
    };
    consumed?: number;
    amount?: number;
};

type Props = {
    boletos: Boleto[];
    onSelect: (boleto: Boleto) => void;
};

export default function MshipBoletos({
    boletos,
    onSelect,
}: Props): React.ReactElement {
    const { t } = useTranslation();

    // Empty state
    if (!boletos || boletos.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    color: 'text.secondary',
                }}
            >
                <CardGiftcard sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body1">
                    {t('generic.noBonosAssigned')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <Grid container spacing={2}>
                {boletos.map((boleto, idx) => {
                    const consumed = boleto.consumed || 0;
                    const total = boleto.amount || 1;
                    const percentage = (consumed / total) * 100;

                    return (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        boxShadow: 2,
                                        borderColor: 'primary.dark',
                                    },
                                }}
                                onClick={() => onSelect(boleto)}
                            >
                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        p: 2,
                                        '&:last-child': { pb: 2 },
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            mb: 1,
                                            bgcolor: 'warning.light',
                                        }}
                                    >
                                        <CardGiftcard
                                            sx={{
                                                fontSize: 20,
                                                color: 'warning.main',
                                            }}
                                        />
                                    </Avatar>
                                    <Tooltip
                                        title={boleto.bono?.name || ''}
                                        arrow
                                        placement="top"
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={500}
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                mb: 1,
                                            }}
                                        >
                                            {boleto.bono?.name}
                                        </Typography>
                                    </Tooltip>
                                    <Box sx={{ width: '100%', mb: 0.5 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={percentage}
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                bgcolor: 'grey.200',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 3,
                                                    bgcolor:
                                                        percentage >= 100
                                                            ? 'error.main'
                                                            : percentage >= 75
                                                              ? 'warning.main'
                                                              : 'success.main',
                                                },
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {consumed} / {total}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
}
